/** Level camera. Positive pitch looks up; 0 faces the property facade. */
export const STREET_VIEW_PITCH = 0;

const STREET_VIEW_FOV = 90;
/** About one meter. Closer than this, the pano is on the property. */
const SAME_POINT_DEGREES = 1e-5;

export type StreetViewPov = {
  heading: number;
  pitch: number;
};

/**
 * Compass heading in degrees from the Street View camera to the property.
 * 0 is north, 90 is east. Uses atan2 of the east/north delta.
 */
export function streetViewHeadingDegrees(
  cameraLatitude: number,
  cameraLongitude: number,
  propertyLatitude: number,
  propertyLongitude: number,
): number {
  const dLng = ((propertyLongitude - cameraLongitude) * Math.PI) / 180;
  const cameraLat = (cameraLatitude * Math.PI) / 180;
  const propertyLat = (propertyLatitude * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(propertyLat);
  const x =
    Math.cos(cameraLat) * Math.sin(propertyLat) -
    Math.sin(cameraLat) * Math.cos(propertyLat) * Math.cos(dLng);
  const degrees = (Math.atan2(y, x) * 180) / Math.PI;
  return (degrees + 360) % 360;
}

export function streetViewCameraIsOffset(
  cameraLatitude: number,
  cameraLongitude: number,
  propertyLatitude: number,
  propertyLongitude: number,
): boolean {
  return (
    Math.abs(cameraLatitude - propertyLatitude) > SAME_POINT_DEGREES ||
    Math.abs(cameraLongitude - propertyLongitude) > SAME_POINT_DEGREES
  );
}

/** POV for a Street View panorama standing at the camera, looking at the property. */
export function streetViewPovTowardProperty(
  cameraLatitude: number,
  cameraLongitude: number,
  propertyLatitude: number,
  propertyLongitude: number,
): StreetViewPov {
  const rounded =
    Math.round(
      streetViewHeadingDegrees(
        cameraLatitude,
        cameraLongitude,
        propertyLatitude,
        propertyLongitude,
      ),
    ) % 360;
  return {
    heading: rounded < 0 ? rounded + 360 : rounded,
    pitch: STREET_VIEW_PITCH,
  };
}

function finitePair(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): { latitude: number; longitude: number } | null {
  if (
    latitude == null ||
    longitude == null ||
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude)
  ) {
    return null;
  }
  return { latitude, longitude };
}

export function buildPropertyStreetViewImageUrl(options: {
  apiKey?: string | null;
  propertyLatitude?: number | null;
  propertyLongitude?: number | null;
  cameraLatitude?: number | null;
  cameraLongitude?: number | null;
  panoId?: string | null;
  address?: string | null;
  size?: string;
}): string | null {
  const apiKey = options.apiKey?.trim();
  if (!apiKey) return null;

  const property = finitePair(
    options.propertyLatitude,
    options.propertyLongitude,
  );
  const camera = finitePair(options.cameraLatitude, options.cameraLongitude);
  const address = options.address?.trim() || "";
  const panoId = options.panoId?.trim() || "";

  const aimed =
    camera != null &&
    property != null &&
    streetViewCameraIsOffset(
      camera.latitude,
      camera.longitude,
      property.latitude,
      property.longitude,
    );

  let location: string | null = null;
  if (aimed && camera) {
    location = `${camera.latitude},${camera.longitude}`;
  } else if (property) {
    location = `${property.latitude},${property.longitude}`;
  } else if (address) {
    location = address;
  }
  if (!location && !panoId) return null;

  const params = new URLSearchParams();
  params.set("size", options.size ?? "400x200");
  if (panoId && aimed) params.set("pano", panoId);
  if (location) params.set("location", location);
  if (aimed && camera && property) {
    const pov = streetViewPovTowardProperty(
      camera.latitude,
      camera.longitude,
      property.latitude,
      property.longitude,
    );
    params.set("heading", String(pov.heading));
    params.set("pitch", String(pov.pitch));
  } else {
    params.set("pitch", String(STREET_VIEW_PITCH));
  }
  params.set("fov", String(STREET_VIEW_FOV));
  params.set("key", apiKey);
  return `https://maps.googleapis.com/maps/api/streetview?${params.toString()}`;
}

/** Opens the same camera in Google Maps, aimed at the property. */
export function buildPropertyStreetViewPanoLink(options: {
  propertyLatitude?: number | null;
  propertyLongitude?: number | null;
  cameraLatitude?: number | null;
  cameraLongitude?: number | null;
  address?: string | null;
}): string | null {
  const property = finitePair(
    options.propertyLatitude,
    options.propertyLongitude,
  );
  const camera = finitePair(options.cameraLatitude, options.cameraLongitude);
  if (
    camera &&
    property &&
    streetViewCameraIsOffset(
      camera.latitude,
      camera.longitude,
      property.latitude,
      property.longitude,
    )
  ) {
    const pov = streetViewPovTowardProperty(
      camera.latitude,
      camera.longitude,
      property.latitude,
      property.longitude,
    );
    return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${camera.latitude},${camera.longitude}&heading=${pov.heading}&pitch=${pov.pitch}&fov=${STREET_VIEW_FOV}`;
  }
  const address = options.address?.trim();
  if (address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }
  if (property) {
    return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${property.latitude},${property.longitude}&fov=${STREET_VIEW_FOV}`;
  }
  return null;
}
